/**
 * Sign in — visual port of mobile app/(auth)/sign-in.tsx (logo, "Log in",
 * 56px inputs with the eye toggle, primary button, underlined forgot link)
 * plus the provider buttons from components/auth/AuthActionSheet.tsx.
 * Sign-up happens in the mobile app, so the bottom line points there.
 * Auth logic is unchanged from the previous web sign-in.
 */

import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/auth/AuthProvider'
import { Ionicon, type IoniconName } from '@/components/ui/Ionicon'
import { AlertDialog, type AlertState } from '@/components/settings/SettingsUI'

type TFn = (key: string, fallback: string) => string

function friendlyError(err: unknown, t: TFn): string {
  const code = (err as { code?: string })?.code || ''
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return t('auth.checkCredentials', 'Unable to log in. Please check your credentials.')
    case 'auth/invalid-email':
      return t('auth.validation.invalidEmail', 'Please enter a valid email')
    case 'auth/too-many-requests':
      return t('settings.changePassword.errors.tooManyRequests', 'Too many attempts. Please try again later.')
    case 'auth/account-exists-with-different-credential':
      // Cross-provider dead end (audit #5): tell them WHICH way out.
      return t('webapp.authErrors.accountExists', 'An account already exists for this email with a different sign-in method. Try the method you used in the app — email & password, Google, or Apple.')
    case 'auth/popup-blocked':
      return t('webapp.authErrors.popupBlocked', 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.')
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return ''
    default:
      return t('webapp.log.error', 'Something went wrong. Please try again.')
  }
}

export function SignIn() {
  const { t } = useTranslation()
  const { signInEmail, signInGoogle, signInApple, resetPassword } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [busy, setBusy] = useState<null | 'email' | 'google' | 'apple' | 'reset'>(null)
  const [alert, setAlert] = useState<AlertState | null>(null)

  const isFormValid = email.trim().length > 0 && password.length > 0

  async function run(kind: 'email' | 'google' | 'apple' | 'reset', title: string, fn: () => Promise<void>) {
    setBusy(kind)
    try {
      await fn()
      // On success the auth listener flips state and the router redirects.
    } catch (err) {
      const msg = friendlyError(err, t)
      if (msg) setAlert({ title, message: msg })
    } finally {
      setBusy(null)
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!isFormValid || busy) return
    void run('email', t('auth.loginFailed', 'Login Failed'), () => signInEmail(email.trim(), password))
  }

  async function onForgot() {
    if (!email.trim()) {
      setAlert({ title: t('auth.forgotPassword', 'Forgot your password?'), message: t('auth.enterEmailFirst', 'Please enter your email address first') })
      return
    }
    await run('reset', t('auth.resetFailed', 'Reset Failed'), async () => {
      await resetPassword(email.trim())
      // Email-enumeration protection may be on, so this resolves even when no
      // account exists: the copy must not promise an email (mobile parity).
      setAlert({
        title: t('auth.resetRequestedTitle', 'Password reset requested'),
        message: [
          t('auth.checkInboxForReset', { email: email.trim() }),
          t('auth.resetSocialHint', {
            apple: t('authSheet.continueWithApple', 'Continue with Apple'),
            google: t('authSheet.continueWithGoogle', 'Continue with Google'),
          }),
        ].join('\n\n'),
      })
    })
  }

  const inputClass =
    'h-[56px] w-full rounded-[12px] border-[1.5px] border-input-border bg-input-background px-[20px] text-[16px] text-input-text outline-none transition-colors placeholder:text-input-placeholder focus:border-primary'

  const providerButton = (kind: 'google' | 'apple', icon: IoniconName, label: string, fn: () => Promise<void>) => (
    <button
      type="button"
      onClick={() => void run(kind, t('authSheet.errors.signInFailed', 'Sign In Failed'), fn)}
      disabled={busy !== null}
      className="flex items-center justify-center rounded-[12px] border border-border px-lg py-md text-text transition-opacity hover:bg-text/[0.03] active:opacity-80 disabled:opacity-60"
    >
      {busy === kind ? (
        <Spinner />
      ) : (
        <>
          <span className="me-smd flex w-[24px] items-center justify-center">
            <Ionicon name={icon} size={24} />
          </span>
          <span className="text-[16px] font-semibold leading-[normal]">{label}</span>
        </>
      )}
    </button>
  )

  return (
    <div className="flex min-h-full w-full flex-col bg-bg">
      <div className="mx-auto flex w-full max-w-content flex-1 flex-col px-[30px] pt-[60px] md:justify-center md:pt-xl">
        {/* Logo */}
        <div className="mb-[40px] flex justify-center">
          <img src="/icon-192.png" alt="Liverly" width={80} height={80} className="h-[80px] w-[80px] rounded-[18px]" />
        </div>

        {/* Header */}
        <h1 className="mb-[30px] text-[32px] font-bold leading-[normal] text-text">{t('auth.logIn', 'Log in')}</h1>

        <form onSubmit={onSubmit} noValidate>
          <div className="mb-md">
            <input
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              placeholder={t('auth.email', 'Email')}
              aria-label={t('auth.email', 'Email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy !== null}
              className={inputClass}
            />
          </div>

          <div className="relative mb-md">
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              autoCapitalize="none"
              placeholder={t('auth.password', 'Password')}
              aria-label={t('auth.password', 'Password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy !== null}
              className={inputClass + ' pe-[56px]'}
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              disabled={busy !== null}
              aria-pressed={showPassword}
              aria-label={t('auth.password', 'Password')}
              className="absolute end-md top-md p-xs text-input-placeholder"
            >
              <Ionicon name={showPassword ? 'eye-off-outline' : 'eye-outline'} size={24} />
            </button>
          </div>

          <button
            type="submit"
            disabled={!isFormValid || busy !== null}
            className="mt-sm flex h-[56px] w-full items-center justify-center rounded-[12px] text-[18px] font-semibold leading-[normal] text-button-primary-text transition-opacity active:opacity-80"
            style={{ backgroundColor: isFormValid ? 'var(--color-primary)' : 'var(--color-button-disabled)' }}
          >
            {busy === 'email' ? <Spinner light /> : t('auth.logIn', 'Log in')}
          </button>
        </form>

        <button
          type="button"
          onClick={() => void onForgot()}
          disabled={busy !== null}
          className="mx-auto mt-mlg text-[16px] font-medium leading-[normal] text-primary underline"
        >
          {t('auth.forgotPassword', 'Forgot your password?')}
        </button>

        {/* Other sign-in methods (mobile AuthActionSheet) */}
        <div className="my-lg flex items-center gap-md">
          <span className="h-px flex-1 bg-border" />
          <span className="text-[13px] leading-[normal] text-text-secondary">{t('common.or', 'or')}</span>
          <span className="h-px flex-1 bg-border" />
        </div>
        <div className="flex flex-col gap-smd">
          {providerButton('google', 'logo-google', t('authSheet.continueWithGoogle', 'Continue with Google'), signInGoogle)}
          {providerButton('apple', 'logo-apple', t('authSheet.continueWithApple', 'Continue with Apple'), signInApple)}
        </div>

        {/* Terms and Privacy */}
        <div className="mt-mlg text-center text-[13px] leading-[18px] text-text">
          <span className="opacity-60">{t('authSheet.termsPrefix', 'By continuing, you agree to our')}</span>
          <br />
          <a href="https://liverly.app/terms" target="_blank" rel="noopener noreferrer" className="underline">
            {t('authSheet.termsOfService', 'Terms of Service')}
          </a>
          <span className="opacity-60"> {t('authSheet.and', 'and')} </span>
          <a href="https://liverly.app/privacy" target="_blank" rel="noopener noreferrer" className="underline">
            {t('authSheet.privacyPolicy', 'Privacy Policy')}
          </a>
        </div>

        <div className="min-h-lg flex-1 md:flex-none" />

        {/* Create account lives in the mobile app */}
        <p className="pb-[30px] pt-lg text-center text-[16px] leading-[22px] text-text-secondary">
          {t('webapp.authNoSignup', 'New to Liverly? Create your account in the mobile app, then sign in here.')}
        </p>
      </div>

      <AlertDialog
        open={alert !== null}
        title={alert?.title ?? ''}
        message={alert?.message}
        buttons={alert?.buttons ?? [{ text: t('common.ok', 'OK') }]}
        onClose={() => setAlert(null)}
      />
    </div>
  )
}

function Spinner({ light }: { light?: boolean }) {
  return (
    <span
      className={
        'h-5 w-5 animate-spin rounded-full border-2 ' +
        (light ? 'border-button-primary-text/40 border-t-button-primary-text' : 'border-border border-t-text')
      }
    />
  )
}
